// src/pages/Home/Home.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../../styles/Pages.css';
import './Home.css';
import { courseData } from '../../data/CoursesData';
import Marquee from '../../components/Marquee/Marquee';

// Hero Slider Images (Static fallback)
const sliderImages = [
  { id: 1, src: '/images/slider/hero-1.jpeg', title: 'Technical Training Excellence', subtitle: "Building Pakistan's skilled workforce" },
  { id: 2, src: '/images/slider/hero-2.jpeg', title: 'Industry-Aligned Programs', subtitle: 'Precision-crafted for career success' },
  { id: 3, src: '/images/slider/hero-3.jpeg', title: 'International Standards', subtitle: 'Globally recognized certifications' },
];

// Officials Data
const officials = [
  {
    id: 1,
    name: 'Mr. Haroon Akhtar Khan',
    position: 'Special Assistant to Prime Minister',
    image: '/images/officials/chairman.png',
    bio: 'Ministry Of Industries & Production'
  },
  {
    id: 2,
    name: 'Mr. Ahsan Ali Mangi',
    position: 'Federal Secretary for Industries & Production',
    image: '/images/officials/secretary.jpg',
    bio: 'Ministry Of Industries & Production'
  },
  {
    id: 3,
    name: 'Ms. Fareeha Ali Randhawa',
    position: 'Director General, PITAC',
    image: '/images/officials/dg.jpg',
    bio: 'Ministry Of Industries & Production'
  },
];

// ✅ Regional Centres Data
const regionalCentres = [
  {
    id: 1,
    province: 'Balochistan',
    city: 'Quetta',
    image: '/images/regional/quetta.jpg',
  },
  {
    id: 2,
    province: 'Sindh',
    city: 'Karachi',
    image: '/images/regional/karachi.jpg',
  },
  {
    id: 3,
    province: 'Azad Kashmir',
    city: 'Mirpur',
    image: '/images/regional/ajk.jpg',
  },
  {
    id: 4,
    province: 'KPK',
    city: 'Peshawar',
    image: '/images/regional/kpk.jpg',
  },
  {
    id: 5,
    province: 'Gilgit',
    city: 'Gilgit',
    image: '/images/regional/gilgit.jpg',
  },
];

// TVET Features Data
const tvetFeatures = [
  { icon: '🎓', title: 'Training Excellence', description: "Pakistan's premier technical & managerial trainings" },
  { icon: '🔧', title: 'Hands-on Skills', description: 'Practical, industry-driven curriculum' },
  { icon: '🌍', title: 'Global Standards', description: 'International certification pathways' },
  { icon: '👥', title: 'Workforce Development', description: 'Empowering youth for economic growth' },
];

// ✅ Fallback testimonials so the section is NEVER blank
const fallbackTestimonials = [
  {
    id: 'fallback-1',
    name: 'Muhammad Bilal',
    role: 'CNC Machinist — Lahore',
    content: 'PITAC gave me the practical skills and confidence to start my career in manufacturing. The instructors were industry experts and the labs were fully equipped.',
    rating: 5,
  },
  {
    id: 'fallback-2',
    name: 'Ayesha Siddiqui',
    role: 'Electrical Technician — Karachi',
    content: 'The PLC & SCADA course transformed my career. I was hired within three weeks of graduation. Highly recommended for anyone serious about technical skills.',
    rating: 5,
  },
  {
    id: 'fallback-3',
    name: 'Hassan Ahmed',
    role: 'Solar Installer — Islamabad',
    content: 'Hands-on training, real-world projects, and job placement support. PITAC truly delivers on its promise of building Pakistan\'s skilled workforce.',
    rating: 5,
  },
];

// Helper function to get color for category
const getCategoryColor = (category) => {
  const colors = {
    'Designing (Mechanical)': '#2563eb',
    'Mechanical Manufacturing': '#7c3aed',
    'Civil': '#059669',
    'Welding & Fabrication': '#d97706',
    'Electrical & Electronics': '#0891b2',
    'Language': '#dc2626',
    'Health & Safety': '#8b5cf6',
    'EV': '#ec4899',
    'AI & Technology': '#f59e0b',
    'Foundry & Metal Treatment': '#14b8a6',
    'Pattern, Carpenter & CNC Wood Router': '#8b4513',
    'Renewable Energy (Solar)': '#22c55e'
  };
  return colors[category] || '#4b5563';
};

const Home = () => {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pressReleases, setPressReleases] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [officialsSlide, setOfficialsSlide] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [dynamicSliders, setDynamicSliders] = useState(sliderImages);

  // Course slider refs
  const courseSliderRef = useRef(null);
  const courseTrackRef = useRef(null);
  const courseAnimationRef = useRef(null);
  const coursePositionRef = useRef(0);
  const isCoursePausedRef = useRef(false);
  const coursePauseTimeoutRef = useRef(null);

  // Partners auto-scroll refs
  const partnersViewportRef = useRef(null);
  const partnersTrackRef = useRef(null);
  const positionRef = useRef(0);
  const isPausedRef = useRef(false);
  const pauseTimeoutRef = useRef(null);
  const rafIdRef = useRef(null);
  const [isPartnerAutoScroll, setIsPartnerAutoScroll] = useState(true);

  // Get unique categories from course data (using API courses or fallback)
  const getCourseCategories = () => {
    const data = courses.length > 0 ? courses : courseData;
    return ['All Categories', ...new Set(data.map(course => course.category))];
  };

  const categories = getCourseCategories();

  const getFilteredCourses = () => {
    const data = courses.length > 0 ? courses : courseData;
    if (selectedCategory === 'All Categories') {
      return data;
    }
    return data.filter(course => course.category === selectedCategory);
  };

  const getVisibleCourses = () => getFilteredCourses();

  // ✅ Compute testimonials to display: use API ones if present, else fallback
  const displayedTestimonials =
    Array.isArray(testimonials) && testimonials.length > 0
      ? testimonials.slice(0, 3)
      : fallbackTestimonials;

  // Course auto-scroll effect
  useEffect(() => {
    if (loading) return;

    const track = courseTrackRef.current;
    if (!track || getVisibleCourses().length === 0) return;

    const speed = 0.8;

    const animate = () => {
      if (!isCoursePausedRef.current && track) {
        const cardWidth = 220;
        const totalWidth = getVisibleCourses().length * cardWidth;
        const visibleWidth = courseSliderRef.current?.clientWidth || 0;
        
        if (totalWidth > visibleWidth) {
          coursePositionRef.current += speed;
          
          if (coursePositionRef.current >= totalWidth - visibleWidth) {
            coursePositionRef.current = 0;
          }
          
          track.style.transform = `translateX(-${coursePositionRef.current}px)`;
        }
      }
      courseAnimationRef.current = requestAnimationFrame(animate);
    };

    courseAnimationRef.current = requestAnimationFrame(animate);
    return () => {
      if (courseAnimationRef.current) cancelAnimationFrame(courseAnimationRef.current);
    };
  }, [loading, selectedCategory, courses]);

  const handleCourseMouseEnter = () => {
    isCoursePausedRef.current = true;
    if (coursePauseTimeoutRef.current) clearTimeout(coursePauseTimeoutRef.current);
  };

  const handleCourseMouseLeave = () => {
    if (coursePauseTimeoutRef.current) clearTimeout(coursePauseTimeoutRef.current);
    coursePauseTimeoutRef.current = setTimeout(() => {
      isCoursePausedRef.current = false;
    }, 500);
  };

  // Partners auto-scroll effect
  useEffect(() => {
    if (loading || partners.length === 0 || !isPartnerAutoScroll) return;

    const track = partnersTrackRef.current;
    if (!track) return;

    const speed = 0.6;

    const animate = () => {
      if (!isPausedRef.current && track) {
        positionRef.current += speed;

        const halfWidth = track.scrollWidth / 2;
        if (halfWidth > 0 && positionRef.current >= halfWidth) {
          positionRef.current -= halfWidth;
        }

        track.style.transform = `translateX(-${positionRef.current}px)`;
      }
      rafIdRef.current = requestAnimationFrame(animate);
    };

    rafIdRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [loading, partners, isPartnerAutoScroll]);

  const scrollPartnersNext = () => {
    const track = partnersTrackRef.current;
    if (!track) return;

    setIsPartnerAutoScroll(false);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    isPausedRef.current = true;

    const jump = 300;
    const halfWidth = track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    positionRef.current += jump;
    if (positionRef.current >= halfWidth) {
      positionRef.current -= halfWidth;
    }

    track.style.transition = 'transform 0.5s ease';
    track.style.transform = `translateX(-${positionRef.current}px)`;
    
    setTimeout(() => {
      if (track) track.style.transition = '';
    }, 500);

    setTimeout(() => {
      isPausedRef.current = false;
      setIsPartnerAutoScroll(true);
    }, 3000);
  };

  const scrollPartnersPrev = () => {
    const track = partnersTrackRef.current;
    if (!track) return;

    setIsPartnerAutoScroll(false);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    isPausedRef.current = true;

    const jump = 300;
    const halfWidth = track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    positionRef.current -= jump;
    if (positionRef.current < 0) {
      positionRef.current += halfWidth;
    }

    track.style.transition = 'transform 0.5s ease';
    track.style.transform = `translateX(-${positionRef.current}px)`;
    
    setTimeout(() => {
      if (track) track.style.transition = '';
    }, 500);

    setTimeout(() => {
      isPausedRef.current = false;
      setIsPartnerAutoScroll(true);
    }, 3000);
  };

  const handlePartnersMouseEnter = () => {
    isPausedRef.current = true;
    setIsPartnerAutoScroll(false);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
  };

  const handlePartnersMouseLeave = () => {
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
      setIsPartnerAutoScroll(true);
    }, 1500);
  };

  // ===== FETCH DATA FROM API =====
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching home data...');
        
        const [homeData, testimonialsRes, pressRes, partnersRes, coursesRes] = await Promise.all([
          axios.get('https://pitac.gov.pk/api/home-data'),
          axios.get('https://pitac.gov.pk/api/testimonials'),
          axios.get('https://pitac.gov.pk/api/press-releases'),
          axios.get('https://pitac.gov.pk/api/partners'),
          axios.get('https://pitac.gov.pk/api/courses')
        ]);
        
        const apiSliders = homeData.data.sliders || [];
        if (apiSliders.length > 0) {
          const formattedSliders = apiSliders.map((slider) => {
            let imageUrl = slider.image_path;
            if (imageUrl && imageUrl.startsWith('/storage/')) {
              imageUrl = `https://pitac.gov.pk${imageUrl}`;
            }
            return {
              id: slider.id,
              src: imageUrl || '/images/slider/default.jpg',
              title: slider.title || 'Slide',
              subtitle: slider.subtitle || '',
              button_text: slider.button_text || 'Apply Now',
              button_link: slider.button_link || '/courses',
            };
          });
          setDynamicSliders(formattedSliders);
          setCurrentSlide(0);
        } else {
          setDynamicSliders(sliderImages);
        }
        
        const apiCourses = coursesRes.data || [];
        if (apiCourses.length > 0) {
          const formattedCourses = apiCourses.map((course) => ({
            id: course.id,
            name: course.title || course.name,
            category: course.category,
            description: course.description || '',
            duration: course.duration,
            fee: course.fee || '',
            level: course.level || 'Beginner',
            is_active: course.is_active,
            is_featured: course.is_featured,
          }));
          setCourses(formattedCourses);
        } else {
          setCourses(courseData);
        }
        
        const apiPartners = partnersRes.data || [];
        if (apiPartners.length > 0) {
          const activePartners = apiPartners.filter(p => p.is_active === true || p.is_active === 1);
          const formattedPartners = activePartners.map((partner) => {
            let logoUrl = partner.logo_path;
            if (logoUrl && logoUrl.startsWith('/storage/')) {
              logoUrl = `https://pitac.gov.pk${logoUrl}`;
            }
            return {
              id: partner.id,
              name: partner.name,
              logo: logoUrl || null,
              website: partner.website || '',
              icon: '🏢',
              is_active: partner.is_active
            };
          });
          setPartners(formattedPartners);
        } else {
          setPartners([]);
        }
        
        setTestimonials(testimonialsRes.data.data || []);
        setPressReleases(pressRes.data.data || []);
        setLoading(false);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setCourses(courseData);
        setPartners([]);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // ===== AUTO-SLIDE FOR HERO =====
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % dynamicSliders.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [dynamicSliders.length]);

  // ===== AUTO-SLIDE FOR OFFICIALS =====
  useEffect(() => {
    const timer = setInterval(() => {
      setOfficialsSlide((prev) => (prev + 1) % Math.ceil(officials.length / 3));
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index) => setCurrentSlide(index);
  const goToOfficialSlide = (index) => setOfficialsSlide(index);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader-container">
          <div className="gov-loader">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Loading PITAC Portal...</p>
        </div>
      </div>
    );
  }

  const getVisibleOfficials = () => {
    const start = officialsSlide * 3;
    return officials.slice(start, start + 3);
  };

  const getCourseIcon = (category) => {
    const icons = {
      'Designing (Mechanical)': '📐',
      'Mechanical Manufacturing': '⚙️',
      'Civil': '🏗️',
      'Welding & Fabrication': '🔧',
      'Electrical & Electronics': '⚡',
      'Language': '🌐',
      'Health & Safety': '🛡️',
      'EV': '🚗',
      'AI & Technology': '🤖',
      'Foundry & Metal Treatment': '🔥',
      'Pattern, Carpenter & CNC Wood Router': '🪚',
      'Renewable Energy (Solar)': '☀️'
    };
    return icons[category] || '📘';
  };

  return (
    <div className="home-page">
      {/* Top Bar - Government Style */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="gov-badge">
              <span className="gov-flag">🇵🇰</span>
              <span className="gov-text">GOVERNMENT OF PAKISTAN</span>
            </div>
            <div className="top-links">
              <a href="#" className="top-link">Citizen Portal</a>
              <span className="top-divider">|</span>
              <a href="#" className="top-link">e-Services</a>
              <span className="top-divider">|</span>
              <a href="#" className="top-link">TVET Dashboard</a>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MARQUEE (after navbar) ===== */}
      <Marquee />

      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="slider-container">
          {dynamicSliders.map((slide, index) => (
            <div 
              key={slide.id || index} 
              className={`slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.src})` }}
            >
              <div className="slide-overlay"></div>
              <div className="slide-content">
                <div className="slide-badge">PITAC</div>
                <h1 className="slide-title">{slide.title}</h1>
                <p className="slide-subtitle">{slide.subtitle}</p>
                <div className="slide-buttons">
                  <button 
                    className="btn-primary" 
                    onClick={() => window.location.href = slide.button_link || '/courses'}
                  >
                    {slide.button_text || 'Apply Now'}
                  </button>
                  <button className="btn-secondary">View Programs</button>
                </div>
              </div>
            </div>
          ))}
          
          <div className="slider-dots">
            {dynamicSliders.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>

          <button 
            className="slider-arrow prev"
            onClick={() => goToSlide((currentSlide - 1 + dynamicSliders.length) % dynamicSliders.length)}
          >
            ‹
          </button>
          <button 
            className="slider-arrow next"
            onClick={() => goToSlide((currentSlide + 1) % dynamicSliders.length)}
          >
            ›
          </button>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="container">
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">32,000+</span>
                <span className="stat-label">Trained Graduates</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">68%</span>
                <span className="stat-label">Employability Rate</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">58+</span>
                <span className="stat-label">Technical Programs</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">90+</span>
                <span className="stat-label">Industry Partners</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TVET Highlights Section */}
      <section className="tvet-highlight">
        <div className="container">
          <div className="tvet-grid">
            {tvetFeatures.map((feature, index) => (
              <div key={index} className="tvet-item">
                <div className="tvet-icon">{feature.icon}</div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="who-we-are">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">About PITAC</span>
            <h2>Who We Are</h2>
            <p className="section-subtitle">
              The Government of Pakistan established Pakistan Industrial Technical Assistance Centre (PITAC) in 1962 with the merger of Industrial Research and Development Centre (IRDC) and Industrial Productivity Centre (IPC) under the administrative control of Ministry of Industries, Government of Pakistan.
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-number">01</div>
              <div className="feature-icon">
                <img src="/images/target-arrow.jpeg" alt="Mission Target" style={{ width: '90px', height: 'auto' }} />
              </div>
              <h3>Mission Statement</h3>
              <p>
                To upgrade, advise, and extend technical & managerial skill development 
                for sustainable industrial growth and economic value.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">02</div>
              <div className="feature-icon">
                <img src="/images/eye-vision.png" alt="Vision Eye" style={{ width: '90px', height: 'auto' }} />
              </div>
              <h3>Vision Statement</h3>
              <p>
                To attain excellence as the premier organization in technical human resource 
                development and industrial spares manufacturing.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">03</div>
              <div className="feature-icon">
                <img src="/images/team-target.png" alt="Core Values Team" style={{ width: '90px', height: 'auto' }} />
              </div>
              <h3>Core Values</h3>
              <p>
                To establish PITAC as Pakistan's premier technical training institution with world-class facilities, industry-relevant diplomas, corporate training, customized assistance, and a commitment to accessible education and sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Officials Slider Section */}
      <section className="officials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Leadership</span>
            <h2>Our Leadership Team</h2>
            <p className="section-subtitle">Guiding Pakistan's technical education & vocational training</p>
          </div>
          <div className="officials-slider">
            <div className="officials-grid">
              {getVisibleOfficials().map((official) => (
                <div key={official.id} className="official-card">
                  <div className="official-image-wrapper">
                    <img 
                      src={official.image} 
                      alt={official.name} 
                      className="official-image"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UyZTJlMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjQwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmaWxsPSIjOTk5Ij5QVEM8L3RleHQ+PC9zdmc+';
                      }}
                    />
                    <div className="official-overlay">
                      <span className="official-role-badge">{official.position}</span>
                    </div>
                  </div>
                  <h3 className="official-name">{official.name}</h3>
                  <p className="official-position">{official.position}</p>
                  <p className="official-bio">{official.bio}</p>
                </div>
              ))}
            </div>

            <div className="officials-dots">
              {[...Array(Math.ceil(officials.length / 3))].map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === officialsSlide ? 'active' : ''}`}
                  onClick={() => goToOfficialSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          REGIONAL CENTRES SECTION  ← NEW
          ============================================ */}
      <section className="regional-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Network</span>
            <h2>Regional <span className="highlight-text">Centres</span></h2>
            <p className="section-subtitle">
              PITAC's presence across Pakistan — connecting industry, skills & opportunity
            </p>
          </div>

          <div className="regional-grid">
            {regionalCentres.map((centre) => (
              <div key={centre.id} className="regional-card">
                <div className="regional-province-badge">
                  <span className="regional-pin">📍</span>
                  {centre.province}
                </div>

                <div className="regional-image-wrapper">
                  <img
                    src={centre.image}
                    alt={`${centre.city} Regional Centre`}
                    className="regional-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src =
                        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI1MCIgZmlsbD0iI2UyZTJlMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjI4IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmaWxsPSIjOTk5Ij5QSVRBQzwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  <div className="regional-image-overlay"></div>
                </div>

                <h3 className="regional-city">{centre.city}</h3>
                <p className="regional-label">Regional Centre</p>

                <div className="regional-card-footer">
                  <span className="regional-footer-dot"></span>
                  <span>Active Facility</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COURSES SLIDER SECTION ===== */}
      <section className="courses-slider-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Programs</span>
            <h2>Industry Ready <span className="highlight-text">Training Programs</span></h2>
            <p className="section-subtitle">
              Explore our comprehensive range of technical and managerial training programs
            </p>
          </div>

          <div className="course-category-filter">
            <div className="filter-wrapper">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`filter-chip ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'All Categories' ? '🏷️ All' : category}
                </button>
              ))}
            </div>
          </div>

          <div 
            className="course-slider-wrapper"
            onMouseEnter={handleCourseMouseEnter}
            onMouseLeave={handleCourseMouseLeave}
          >
            <div className="course-slider-viewport" ref={courseSliderRef}>
              <div className="course-slider-track" ref={courseTrackRef}>
                {getVisibleCourses().map((course) => (
                  <div key={course.id} className="course-slider-card">
                    <div className="course-card-inner">
                      <div className="course-card-header">
                        <span className={`level-badge ${course.level.toLowerCase()}`}>
                          {course.level}
                        </span>
                      </div>
                      
                      <h4 className="course-card-title">{course.name}</h4>
                      
                      <span className="category-tag" style={{
                        background: getCategoryColor(course.category),
                        color: '#fff'
                      }}>
                        {course.category}
                      </span>
                      
                      <div className="course-card-details">
                        <span className="detail-item">
                          <span className="detail-icon">⏱️</span>
                          <span>{course.duration}</span>
                        </span>
                        <span className="detail-item">
                          <span className="detail-icon">💰</span>
                          <span className="fee-amount">Rs. {course.fee}</span>
                        </span>
                      </div>
                      
                      <button className="course-card-btn">
                        Enroll Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="view-all-courses">
            <button className="view-all-btn" onClick={() => window.location.href = '/courses'}>
              View All Programs 
              <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Press Section - News & Events */}
      <section className="press-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">News & Events</span>
            <h2>Latest <span className="highlight-text">Updates</span></h2>
            <p className="section-subtitle">
              Stay informed about PITAC's latest achievements, events, and announcements
            </p>
          </div>

          {pressReleases.length > 0 ? (
            <div className="press-layout">
              {pressReleases.slice(0, 1).map(release => (
                <article
                  key={release.id}
                  className="press-hero-card clickable-card"
                  onClick={() => window.location.href = `/press-releases/${release.id}`}
                >
                  <div className="press-hero-badge">
                    <span className="pulse-dot"></span>
                    Breaking News
                  </div>

                  <div className="press-hero-content">
                    <span className="press-hero-category">📰 Featured Story</span>
                    <h3 className="press-hero-title">{release.title}</h3>
                    <p className="press-hero-summary">{release.summary}</p>

                    <div className="press-hero-footer">
                      <div className="press-hero-meta">
                        <span className="press-hero-date">📅 {release.published_date || '2026'}</span>
                        <span className="press-hero-readtime">⏱️ 3 min read</span>
                      </div>
                      <span className="press-hero-btn">
                        <span>Read Full Story</span>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div className="press-hero-glow"></div>
                </article>
              ))}

              <div className="press-secondary-grid">
                {pressReleases.slice(1, 5).map((release, index) => (
                  <article
                    key={release.id}
                    className={`press-card clickable-card card-${index + 1}`}
                    onClick={() => window.location.href = `/press-releases/${release.id}`}
                  >
                    <div className="press-card-number">0{index + 2}</div>

                    <div className="press-card-tag">
                      <span className="press-card-tag-dot"></span>
                      Latest
                    </div>

                    <h4 className="press-card-title">{release.title}</h4>
                    <p className="press-card-summary">{release.summary}</p>

                    <div className="press-card-divider"></div>

                    <div className="press-card-footer">
                      <span className="press-card-date">
                        📅 {release.published_date || '2026'}
                      </span>
                      <span className="press-card-arrow">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className="press-layout">
              <article
                className="press-hero-card clickable-card"
                onClick={() => window.location.href = '/press-releases/1'}
              >
                <div className="press-hero-badge">
                  <span className="pulse-dot"></span>
                  Breaking News
                </div>
                <div className="press-hero-content">
                  <span className="press-hero-category">📰 Featured Story</span>
                  <h3 className="press-hero-title">PITAC Launches Advanced AI & Robotics Training Program</h3>
                  <p className="press-hero-summary">
                    New state-of-the-art program designed to equip Pakistani youth with
                    cutting-edge artificial intelligence and robotics skills for the future workforce.
                  </p>
                  <div className="press-hero-footer">
                    <div className="press-hero-meta">
                      <span className="press-hero-date">📅 March 15, 2026</span>
                      <span className="press-hero-readtime">⏱️ 3 min read</span>
                    </div>
                    <span className="press-hero-btn">
                      <span>Read Full Story</span>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="press-hero-glow"></div>
              </article>

              <div className="press-secondary-grid">
                {[
                  { id: 2, title: 'Switch Gear Design with AutoCAD', summary: 'Comprehensive course for electrical engineers and technicians to master switch gear design.', date: 'Feb 28, 2026' },
                  { id: 3, title: 'INDUSTRIAL ELECTRICIAN — 100% Job Ready', summary: 'Industry-oriented hands-on training program with 100% job guarantee and certification.', date: 'Feb 10, 2026' },
                  { id: 4, title: 'PLC – HMI – SCADA Training Program', summary: "Pakistan's No.1 Automation Training Centre with latest computerized lab facilities.", date: 'Jan 25, 2026' },
                  { id: 5, title: 'Renewable Energy Training Program', summary: 'Comprehensive training in solar system design, installation, and maintenance.', date: 'Jan 15, 2026' },
                ].map((release, index) => (
                  <article
                    key={release.id}
                    className={`press-card clickable-card card-${index + 1}`}
                    onClick={() => window.location.href = `/press-releases/${release.id}`}
                  >
                    <div className="press-card-number">0{index + 2}</div>
                    <div className="press-card-tag">
                      <span className="press-card-tag-dot"></span>
                      Latest
                    </div>
                    <h4 className="press-card-title">{release.title}</h4>
                    <p className="press-card-summary">{release.summary}</p>
                    <div className="press-card-divider"></div>
                    <div className="press-card-footer">
                      <span className="press-card-date">📅 {release.date}</span>
                      <span className="press-card-arrow">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          <div className="press-view-all-premium">
            <button className="view-all-premium-btn" onClick={() => window.location.href = '/press-releases'}>
              <span>Explore All News</span>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIALS SECTION — PROFESSIONAL
          ============================================ */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Testimonials</span>
            <h2>What Our <span className="highlight-text">Students Say</span></h2>
            <p className="section-subtitle">
              Real stories from graduates who built their careers with PITAC
            </p>
          </div>

          <div className="testimonials-grid">
            {displayedTestimonials.map((testimonial, index) => {
              const rating = testimonial.rating || 5;
              const initial = (testimonial.name || 'P').charAt(0).toUpperCase();
              return (
                <div key={testimonial.id || index} className="testimonial-card">
                  <div className="testimonial-quote-mark">"</div>

                  <div className="testimonial-rating">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className={i < rating ? 'star filled' : 'star'}>
                        ★
                      </span>
                    ))}
                  </div>

                  <p className="testimonial-content">
                    {testimonial.content}
                  </p>

                  <div className="testimonial-divider"></div>

                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.avatar ? (
                        <img src={testimonial.avatar} alt={testimonial.name} />
                      ) : (
                        <span className="avatar-initial">{initial}</span>
                      )}
                    </div>
                    <div className="author-details">
                      <h4>{testimonial.name}</h4>
                      <p className="author-role">{testimonial.role || 'PITAC Graduate'}</p>
                    </div>
                  </div>

                  <div className="testimonial-verified">
                    <span className="verified-dot"></span>
                    <span>Verified Graduate</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <span>Admissions Open 2026</span>
            </div>
            <h2>Ready to Kickstart Your Career?</h2>
            <p className="cta-subtitle">Join Pakistan's premier technical training institution</p>
            <button className="cta-btn">
              <span>Apply Now</span>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
            <div className="cta-features">
              <span>✓ Industry-Recognized Certifications</span>
              <span>✓ Expert Faculty</span>
              <span>✓ Job Placement Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Partners</span>
            <h2>Industry & Academic Partners</h2>
          </div>

          {partners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7a8f' }}>
              <p>No partners added yet.</p>
            </div>
          ) : (
            <div className="partners-wrapper">
              <button 
                className="partner-nav-btn partner-nav-prev"
                onClick={scrollPartnersPrev}
                aria-label="Previous partners"
              >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              <div
                className="partners-slider"
                ref={partnersViewportRef}
                onMouseEnter={handlePartnersMouseEnter}
                onMouseLeave={handlePartnersMouseLeave}
              >
                <div className="partners-track" ref={partnersTrackRef}>
                  {[...partners, ...partners].map((partner, index) => (
                    <div key={`${partner.id}-${index}`} className="partner-item">
                      {partner.logo ? (
                        <img 
                          src={partner.logo} 
                          alt={`${partner.name} logo`} 
                          className="partner-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallbackIcon = e.target.parentElement?.querySelector('.partner-fallback-icon');
                            if (fallbackIcon) fallbackIcon.style.display = 'block';
                          }}
                        />
                      ) : (
                        <span className="partner-fallback-icon">🏢</span>
                      )}
                      <span className="partner-name">{partner.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button 
                className="partner-nav-btn partner-nav-next"
                onClick={scrollPartnersNext}
                aria-label="Next partners"
              >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;